import { BigNumber, ethers, Signer } from 'ethers'
import {
  createPublicClient,
  createWalletClient,
  custom,
  encodeFunctionData,
  WalletClient,
} from 'viem'
import { erc20Abi } from 'viem'
import CommonPool from 'abis/CommonPool.json'
import { sonic } from 'utils/rainbow-config'
import { SendPaymasterTransactionFn } from 'hooks/usePaymaster'
import { getSwapTokens } from 'config/contracts'
import { NATIVE_TOKEN_ADDRESS, Token, TOKENS_MAP } from 'config/tokens'

type Params = {
  account: string
  amount: BigNumber
  setPendingTxns: any
  sendPaymasterTransaction: SendPaymasterTransactionFn
  commonPoolAddress: string
  firstToken?: Token
  commonPoolAsset?: string
}

export async function createCommonPoolDeposit(
  chainId: number,
  signer: Signer,
  p: Params,
) {
  await window.ethereum.request({
    method: 'wallet_switchEthereumChain',
    params: [{ chainId: '0x92' }], // 0x92 = 146 in hex
  })

  const walletClient = createWalletClient({
    transport: custom(window.ethereum),
    chain: sonic,
    account: p.account as `0x${string}`,
  })
  const swapToken = getSwapTokens(chainId, p.commonPoolAddress)?.[0]

  if (
    (swapToken && p.firstToken?.symbol === 'USDC') ||
    p.firstToken?.symbol === 'S'
  ) {
    const toToken = Object.values(TOKENS_MAP[chainId]).find(
      (item) => item.symbol === p.commonPoolAsset,
    )
    if (!toToken) {
      return
    }
    await magpieSwap(
      p.firstToken,
      toToken,
      p.account,
      p.amount,
      walletClient,
      signer,
    )
  }

  const calldata = encodeFunctionData({
    abi: CommonPool.abi,
    args: [BigNumber.from(p.amount), p.account],
    functionName: 'deposit',
  })

  const call: Parameters<typeof p.sendPaymasterTransaction>[0]['call'] = {
    address: p.commonPoolAddress,
    calldata,
  }

  const messageOpts = {
    hideSentMsg: false,
    hideSuccessMsg: false,
  }

  const contract = new ethers.Contract(
    p.commonPoolAddress,
    CommonPool.abi,
    signer,
  )

  return p?.sendPaymasterTransaction({
    call,
    account: p.account,
    messageOpts,
    setPendingTxns: p.setPendingTxns,
    router: contract,
    payload: [BigNumber.from(p.amount), p.account],
    method: 'deposit',
  })

  // console.log(tryTransaction);

  // const calldata = encodeFunctionData({
  //   abi: CommonPool.abi,
  //   args: [BigInt(100000), p.account],
  //   functionName: "deposit",
  // });

  // console.log(calldata);

  // const res = await sendMulticall(
  //   tx,
  //   calldata,
  //   "0x8662a6c0821B2b5c6e71cf97B2C66fA7D06d8B75",
  //   walletClient
  // );
}

/// @ts-ignore
async function getSwapQuote(params) {
  const value = await fetch(
    '/magpie/aggregator/quote?' + new URLSearchParams(params).toString(),
  )
  return await value.json()
}

async function getSwapTx(swapParams: Record<string, string>) {
  const value = await fetch(
    '/magpie/aggregator/transaction?' +
      new URLSearchParams(swapParams).toString(),
  )
  return await value.json()
}

// @ts-ignore
async function maintainApproval(
  tokenAddress: string,
  spender: string,
  receiver: string,
  amount: BigNumber,
  walletClient: WalletClient,
) {
  const publicClient = createPublicClient({
    transport: custom(window.ethereum),
    chain: sonic,
  })
  const allowance = await publicClient.readContract({
    abi: erc20Abi,
    address: tokenAddress as `0x${string}`,
    functionName: 'allowance',
    args: [receiver as `0x${string}`, spender as `0x${string}`],
  })
  if (BigNumber.from(allowance).lt(amount)) {
    await walletClient.writeContract({
      abi: erc20Abi,
      address: tokenAddress as `0x${string}`,
      functionName: 'approve',
      args: [spender as `0x${string}`, amount.toBigInt()],
      chain: undefined,
      account: receiver as `0x${string}`,
    })
  }
}

export const magpieSwap = async (
  firstToken: Token,
  toToken: Token,
  account: string,
  amount: BigNumber,
  walletClient: any,
  signer: Signer,
) => {
  const result = await getSwapQuote({
    network: 'sonic',
    fromTokenAddress: firstToken.address,
    toTokenAddress: toToken?.address,
    fromAddress: account,
    toAddress: account,
    sellAmount: amount,
    slippage: '0.4',
    gasless: false.toString(),
  })

  const swapParams = {
    quoteId: result.id,
    estimateGas: false.toString(),
  }
  if (firstToken.address !== NATIVE_TOKEN_ADDRESS) {
    await maintainApproval(
      firstToken.address,
      result.targetAddress,
      account,
      amount,
      walletClient,
    )
  }
  let tx = await getSwapTx(swapParams)

  await signer.sendTransaction({
    to: tx.to,
    data: tx.data,
    maxFeePerGas: tx.maxFeePerGas,
    maxPriorityFeePerGas: tx.maxPriorityFeePerGas,
    value: tx.value,
  })
}
